import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import ReviewModal from '../components/ReviewModal';

const Reviews = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const reviewsPerPage = 6; // 2 rows x 3 reviews

  // Load reviews from API
  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${window.location.origin}/api/public/reviews`);
      if (response.ok) {
        const data = await response.json();
        // Only show approved reviews
        setReviews(data.filter(review => review.approved));
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  // Refresh reviews after new review is submitted
  const handleReviewSubmitted = () => {
    fetchReviews();
  };

  // Pagination for carousel
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);
  const startIndex = currentPage * reviewsPerPage;
  const visibleReviews = reviews.slice(startIndex, startIndex + reviewsPerPage);

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20">
        <div className="absolute inset-0 bg-black opacity-40 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1620452537803-f50265d4d9aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxibGFjayUyMHdoaXRlJTIwcGhvdG9ncmFwaHl8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzU3NTA3MzU5fDA&ixlib=rb-4.1.0&q=85')`
          }}
        ></div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 
            className="text-5xl md:text-6xl font-bold mb-6"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {t('reviews.title')}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            {t('reviews.subtitle')}
          </p>
        </div>
      </section>

      {/* Reviews Carousel */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-400">{t('reviews.loading', 'Loading reviews...')}</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">{t('reviews.noReviews')}</p>
            </div>
          ) : (
            <>
              {/* Reviews Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
                {visibleReviews.map((review) => (
                  <div 
                    key={review.id} 
                    className="bg-gray-900 p-8 border border-gray-800 hover:border-white transition-all duration-500 group"
                  >
                    <div className="flex items-center mb-6">
                      <Quote className="h-8 w-8 text-gray-600 mr-4" />
                      <div className="flex">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                        ))}
                      </div>
                    </div>
                    
                    <blockquote className="text-lg text-gray-300 mb-6 italic leading-relaxed">
                      "{review[`text_${currentLang}`] || review.text_de}"
                    </blockquote>
                    
                    <div className="border-t border-gray-800 pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 
                            className="text-lg font-semibold text-white"
                            style={{ fontFamily: '"Times New Roman", serif' }}
                          >
                            {review.name}
                          </h4>
                          <p className="text-sm text-gray-400 capitalize">
                            {review.service} • {new Date(review.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center group-hover:bg-white group-hover:text-black transition-all duration-300">
                          <span 
                            className="text-lg font-bold"
                            style={{ fontFamily: '"Times New Roman", serif' }}
                          >
                            {review.name.charAt(0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Carousel Navigation */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center space-x-4">
                  <button
                    onClick={prevPage}
                    className="p-2 border border-gray-700 hover:border-white transition-colors"
                    aria-label="Previous reviews"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  
                  <div className="flex space-x-2">
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentPage(index)}
                        className={`w-3 h-3 rounded-full transition-colors ${
                          index === currentPage ? 'bg-white' : 'bg-gray-600'
                        }`}
                        aria-label={`Go to page ${index + 1}`}
                      />
                    ))}
                  </div>
                  
                  <button
                    onClick={nextPage}
                    className="p-2 border border-gray-700 hover:border-white transition-colors"
                    aria-label="Next reviews"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </div>
              )}
            </>
          )}

          {/* Stats Section */}
          <div className="mt-20 bg-gray-900 p-12 border border-gray-800">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
              <div>
                <div 
                  className="text-4xl font-bold mb-2"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  5.0
                </div>
                <div className="flex justify-center mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <div className="text-sm text-gray-400">{t('reviews.stats.averageRating')}</div>
              </div>
              
              <div>
                <div 
                  className="text-4xl font-bold mb-2"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  200+
                </div>
                <div className="text-sm text-gray-400">{t('reviews.stats.satisfiedCustomers')}</div>
              </div>
              
              <div>
                <div 
                  className="text-4xl font-bold mb-2"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  98%
                </div>
                <div className="text-sm text-gray-400">{t('reviews.stats.recommendation')}</div>
              </div>
              
              <div>
                <div 
                  className="text-4xl font-bold mb-2"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  10+
                </div>
                <div className="text-sm text-gray-400">{t('reviews.stats.experience')}</div>
              </div>
            </div>
          </div>

          {/* Testimonial Form Teaser */}
          <div className="mt-16 text-center">
            <div className="bg-gray-900 p-8 border border-gray-800 max-w-2xl mx-auto">
              <h3 
                className="text-2xl font-bold mb-4"
                style={{ fontFamily: '"Times New Roman", serif' }}
              >
                {t('reviews.shareExperience.title')}
              </h3>
              <p className="text-gray-400 text-lg mb-6">
                {t('reviews.shareExperience.description')}
              </p>
              <button 
                className="px-6 py-3 border border-white text-white hover:bg-white hover:text-black transition-all duration-300"
                onClick={() => setReviewModalOpen(true)}
              >
                {t('reviews.shareExperience.cta')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Review Modal */}
      <ReviewModal
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        onReviewSubmitted={handleReviewSubmitted}
      />
    </div>
  );
};

export default Reviews;